import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, ShoppingBag, ArrowRight, X, Package } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatPrice } from '@/lib/utils';
import { CartItem } from '@/types';

interface AddedToCartModalProps {
  open: boolean;
  onClose: () => void;
  product: {
    id: number;
    produt_name: string;
    sellingprice: string;
    images: string[];
  };
  quantity: number;
  selectedVariations?: Array<{ variation: string; quantity: number }>;
  onContinueShopping: () => void;
  onGoToCart: () => void;
}

export function AddedToCartModal({
  open,
  onClose,
  product,
  quantity,
  selectedVariations,
  onContinueShopping,
  onGoToCart,
}: AddedToCartModalProps) {
  const totalQuantity = selectedVariations 
    ? selectedVariations.reduce((sum, v) => sum + v.quantity, 0)
    : quantity;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.8, opacity: 0, y: 20 }}
            transition={{ type: "spring", duration: 0.5 }}
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Success Header */}
            <div className="bg-gradient-to-r from-green-500 to-emerald-500 p-6 text-white relative overflow-hidden">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring" }}
                className="absolute top-4 right-4"
              >
                <button
                  onClick={onClose}
                  className="p-1 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </motion.div>
              
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.1, type: "spring" }}
                className="flex items-center justify-center mb-4"
              >
                <div className="p-3 bg-white/20 rounded-full">
                  <CheckCircle className="h-8 w-8" />
                </div>
              </motion.div>
              
              <motion.h2
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="text-xl font-bold text-center"
              >
                Added to Cart!
              </motion.h2>
            </div>

            {/* Product Details */}
            <div className="p-6">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 }}
                className="flex items-center gap-4 mb-6"
              >
                <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700">
                  <img
                    src={product.images[0]}
                    alt={product.produt_name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1">
                  <h3 className="font-medium text-gray-900 dark:text-white line-clamp-2">
                    {product.produt_name}
                  </h3>
                  <div className="flex items-center gap-2 mt-1">
                    <Package className="h-4 w-4 text-gray-400" />
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      Qty: {totalQuantity}
                    </span>
                  </div>
                  {selectedVariations && selectedVariations.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {selectedVariations.map((variation, index) => (
                        <div key={index} className="text-xs text-gray-500 dark:text-gray-400">
                          {variation.variation}: {variation.quantity}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="text-right">
                  <span className="text-lg font-bold text-brand-primary dark:text-brand-light">
                    {formatPrice(product.sellingprice)}
                  </span>
                </div>
              </motion.div>

              {/* Action Buttons */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="space-y-3"
              >
                <Button
                  onClick={onGoToCart}
                  className="w-full h-12 text-base font-medium group"
                >
                  <ShoppingBag className="h-5 w-5 mr-2" />
                  View Cart & Checkout
                  <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
                
                <Button
                  variant="outline"
                  onClick={onContinueShopping}
                  className="w-full h-12 text-base"
                >
                  Continue Shopping
                </Button>
              </motion.div>
            </div>

            {/* Floating particles animation */}
            <div className="absolute inset-0 pointer-events-none overflow-hidden">
              {[...Array(6)].map((_, i) => (
                <motion.div
                  key={i}
                  initial={{ 
                    opacity: 0,
                    scale: 0,
                    x: Math.random() * 400,
                    y: Math.random() * 300
                  }}
                  animate={{ 
                    opacity: [0, 1, 0],
                    scale: [0, 1, 0],
                    y: [0, -50, -100]
                  }}
                  transition={{
                    duration: 2,
                    delay: 0.5 + i * 0.1,
                    ease: "easeOut"
                  }}
                  className="absolute w-2 h-2 bg-green-400 rounded-full"
                />
              ))}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}